import React, { useEffect, useRef, useState } from 'react';
import { Card, Form, Select, Button, Typography, message, Spin, Row, Col, Statistic, Table, Modal, Input, Space, Popconfirm, Tag, Collapse, Alert, Progress, App } from 'antd';
import { SettingOutlined, SaveOutlined, PlusOutlined, EditOutlined, DeleteOutlined, CheckCircleOutlined, DatabaseOutlined, CloudDownloadOutlined, ClearOutlined, PlayCircleOutlined, ApiOutlined, LinkOutlined, DisconnectOutlined } from '@ant-design/icons';
import { useLanguage } from '../contexts/LanguageContext';
import { userSettingsApi, systemApi, aiModelApi } from '../services/api';
import { telegramApi } from '../services/api/telegram';
import { osmPreprocessApi } from '../services/api/osmPreprocess';
import { UserSettings, UpdateUserSettings, AiModel, ModelPurpose } from '../types';

const { Title } = Typography;
const { Panel } = Collapse;

const SettingsPage: React.FC = () => {
  const { t, language, setLanguage } = useLanguage();
  const { modal } = App.useApp();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [, setSettings] = useState<UserSettings | null>(null);
  const [form] = Form.useForm();

  // System Export/Import state
  const [exportLoading, setExportLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [systemInfo, setSystemInfo] = useState<any>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // AI Model state
  const [aiModels, setAiModels] = useState<AiModel[]>([]);
  const [aiModelLoading, setAiModelLoading] = useState(false);
  const [isAiModelModalVisible, setIsAiModelModalVisible] = useState(false);
  const [editingAiModel, setEditingAiModel] = useState<AiModel | null>(null);
  const [aiModelForm] = Form.useForm();

  // OSM Preprocessing state
  const [osmStatus, setOsmStatus] = useState<any>(null);
  const [osmLoading, setOsmLoading] = useState(false);
  const [osmPreprocessing, setOsmPreprocessing] = useState(false);

  // Telegram Bot state
  const [telegramStatus, setTelegramStatus] = useState<any>(null);
  const [telegramLoading, setTelegramLoading] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [isWebhookModalVisible, setIsWebhookModalVisible] = useState(false);

  useEffect(() => {
    fetchUserSettings();
    fetchSystemInfo();
    fetchAiModels();
    fetchOsmStatus();
    fetchTelegramStatus();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchUserSettings = async () => {
    try {
      setLoading(true);
      const response = await userSettingsApi.getCurrentUserSettings();
      setSettings(response.data);
      
      // Sync backend settings with frontend
      const backendLanguage = response.data.language as 'en' | 'vi';
      if (backendLanguage !== language) {
        setLanguage(backendLanguage);
      }
    
      form.setFieldsValue({
        language: response.data.language,
  theme: response.data.theme || 'light',
        timezone: response.data.timezone || 'UTC',
        fuelStationLat: response.data.fuelStationLat,
        fuelStationLon: response.data.fuelStationLon
   });
    } catch (error) {
      console.error('Error fetching user settings:', error);
      message.error(t('failed.to.fetch.settings') || 'Failed to fetch settings');
      // Set default values if no settings exist
  form.setFieldsValue({
        language: language,
        theme: 'light',
        timezone: 'UTC',
        fuelStationLat: undefined,
        fuelStationLon: undefined
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (values: UpdateUserSettings) => {
    try {
      setSaving(true);
      // Normalize empty fuel station inputs to undefined so the backend receives null
      const normalizeCoord = (value: number | undefined): number | undefined => {
        if (value == null || String(value).trim() === '') return undefined;
        const num = Number(value);
        return Number.isNaN(num) ? undefined : num;
      };
      const payload: UpdateUserSettings = {
        ...values,
        fuelStationLat: normalizeCoord(values.fuelStationLat),
        fuelStationLon: normalizeCoord(values.fuelStationLon)
      };
      await userSettingsApi.updateCurrentUserSettings(payload);
      if (values.language !== language) {
setLanguage(values.language as 'en' | 'vi');
      }
      message.success(t('settings.saved.successfully') || 'Settings saved successfully');
      await fetchUserSettings();
    } catch (error) {
      console.error('Error saving settings:', error);
      message.error(t('failed.to.save.settings') || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  // System Export/Import logic
  const fetchSystemInfo = async () => {
    try {
      const response = await systemApi.getSystemInfo();
      setSystemInfo(response.data);
    } catch (error) {
      console.error('Error fetching system info:', error);
    }
  };

  const handleExportData = async () => {
    setExportLoading(true);
    try {
      const response = await systemApi.exportAllData();
      const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
      link.href = url;
      const contentDisposition = response.headers['content-disposition'];
      let fileName = 'tps-logistics-export.json';
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename="(.+)"/);
        if (fileNameMatch) {
          fileName = fileNameMatch[1];
        }
      }
 link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
  message.success(t('export.success'));
    } catch (error) {
      console.error('Error exporting data:', error);
      message.error(t('export.error'));
    } finally {
      setExportLoading(false);
 }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.json')) {
message.error(t('invalid.file.type'));
      return;
    }
    setImportLoading(true);
    try {
      const response = await systemApi.importData(file);
      message.success(t('import.success'));
      console.log('Import response:', response.data);
  await fetchSystemInfo();
    } catch (error: any) {
      console.error('Error importing data:', error);
      const errorMessage = error.response?.data?.message || t('import.error');
      message.error(errorMessage);
    } finally {
      setImportLoading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
   }
    }
  };

  // Helper function to get purpose label
  const getPurposeLabel = (purpose: ModelPurpose): string => {
    switch (purpose) {
      case ModelPurpose.DocumentTypeDetection:
        return t('model.purpose.document.type.detection');
      case ModelPurpose.DocumentProcessing:
        return t('model.purpose.document.processing');
      case ModelPurpose.DocumentProcessingPdf:
 return t('model.purpose.document.processing.pdf');
      case ModelPurpose.DocumentProcessingImage:
        return t('model.purpose.document.processing.image');
      case ModelPurpose.TelegramChat:
        return t('model.purpose.telegram.chat');
      default:
return 'Unknown';
    }
  };

  // Helper function to get purpose color
  const getPurposeColor = (purpose: ModelPurpose): string => {
    switch (purpose) {
      case ModelPurpose.DocumentTypeDetection:
        return 'blue';
    case ModelPurpose.DocumentProcessing:
        return 'purple';
      case ModelPurpose.DocumentProcessingPdf:
    return 'cyan';
      case ModelPurpose.DocumentProcessingImage:
        return 'orange';
      case ModelPurpose.TelegramChat:
        return 'green';
      default:
        return 'default';
    }
  };

  // Group models by purpose
  const groupModelsByPurpose = () => {
    const grouped: { [key: number]: AiModel[] } = {};
    aiModels.forEach(model => {
      if (!grouped[model.purpose]) {
        grouped[model.purpose] = [];
      }
      grouped[model.purpose].push(model);
    });
    return grouped;
  };

  // AI Model functions
  const fetchAiModels = async () => {
    try {
      setAiModelLoading(true);
 const response = await aiModelApi.getAll();
  setAiModels(response.data);
    } catch (error) {
      console.error('Error fetching AI models:', error);
  message.error(t('failed.to.fetch.data') || 'Failed to fetch AI models');
    } finally {
  setAiModelLoading(false);
    }
  };

  const handleAddAiModel = () => {
    setEditingAiModel(null);
    aiModelForm.resetFields();
    aiModelForm.setFieldsValue({
      purpose: ModelPurpose.DocumentProcessingPdf // Default to PDF processing
    });
    setIsAiModelModalVisible(true);
  };

  const handleEditAiModel = (model: AiModel) => {
    setEditingAiModel(model);
    aiModelForm.setFieldsValue({
      name: model.name,
      description: model.description,
      purpose: model.purpose
    });
    setIsAiModelModalVisible(true);
  };

  const handleAiModelModalOk = async () => {
    try {
      const values = await aiModelForm.validateFields();
      setAiModelLoading(true);

      if (editingAiModel) {
        // Update existing model
        await aiModelApi.update(editingAiModel.id, values);
        message.success(t('ai.model.updated'));
  } else {
     // Create new model
        await aiModelApi.create(values);
        message.success(t('ai.model.created'));
      }

  setIsAiModelModalVisible(false);
      await fetchAiModels();
    } catch (error: any) {
      console.error('Error saving AI model:', error);
const errorMessage = error.response?.data?.message || (editingAiModel ? t('failed.to.update') : t('failed.to.create'));
      message.error(errorMessage);
    } finally {
      setAiModelLoading(false);
    }
  };

  const handleSelectAiModel = async (modelId: number) => {
    try {
      setAiModelLoading(true);
      await aiModelApi.select({ id: modelId });
      message.success(t('ai.model.selected'));
      await fetchAiModels();
    } catch (error: any) {
      console.error('Error selecting AI model:', error);
      const errorMessage = error.response?.data?.message || t('failed.to.update');
      message.error(errorMessage);
    } finally {
      setAiModelLoading(false);
    }
  };

  const handleDeleteAiModel = async (modelId: number) => {
    try {
      setAiModelLoading(true);
  await aiModelApi.delete(modelId);
 message.success(t('ai.model.deleted'));
      await fetchAiModels();
    } catch (error: any) {
      console.error('Error deleting AI model:', error);
      const errorMessage = error.response?.data?.message || t('failed.to.delete');
    message.error(errorMessage);
    } finally {
      setAiModelLoading(false);
    }
  };

  // OSM Preprocessing functions
  const fetchOsmStatus = async () => {
    try {
      setOsmLoading(true);
 const status = await osmPreprocessApi.getStatus();
      setOsmStatus(status);
    } catch (error) {
  console.error('Error fetching OSM status:', error);
    // Don't show error message as OSM might not be set up yet
    } finally {
      setOsmLoading(false);
    }
  };

  const handleOsmPreprocess = async () => {
    console.log('[OSM] handleOsmPreprocess called');
    console.log('[OSM] osmLoading:', osmLoading, 'osmPreprocessing:', osmPreprocessing);
    
    modal.confirm({
title: t('osm.preprocess.confirm') || 'Start OSM Preprocessing?',
      content: t('osm.preprocessing.in.progress') || 'This will take 15-30 minutes...',
      okText: t('yes') || 'Yes',
    cancelText: t('no') || 'No',
      onOk: async () => {
        console.log('[OSM] Preprocessing confirmed by user');
        try {
       setOsmPreprocessing(true);
   message.info(t('osm.preprocessing.in.progress'));
      console.log('[OSM] Calling osmPreprocessApi.preprocess...');
 await osmPreprocessApi.preprocess(undefined, true);
        console.log('[OSM] Preprocessing completed successfully');
     message.success(t('osm.preprocessing.complete'));
    await fetchOsmStatus();
        } catch (error: any) {
        console.error('[OSM] Error preprocessing OSM data:', error);
          message.error(error.message || t('osm.preprocessing.failed'));
} finally {
          setOsmPreprocessing(false);
   }
},
    });
  };

  const handleOsmClear = async () => {
  console.log('[OSM] handleOsmClear called');
    
    modal.confirm({
    title: t('osm.clear.confirm') || 'Clear all OSM data?',
      okText: t('yes') || 'Yes',
      cancelText: t('no') || 'No',
      okType: 'danger',
      onOk: async () => {
     console.log('[OSM] Clear confirmed by user');
        try {
          setOsmLoading(true);
   console.log('[OSM] Calling osmPreprocessApi.clear...');
 await osmPreprocessApi.clear();
      console.log('[OSM] Clear completed successfully');
  message.success(t('osm.clear.success'));
await fetchOsmStatus();
        } catch (error: any) {
     console.error('[OSM] Error clearing OSM data:', error);
          message.error(error.message || t('osm.clear.failed'));
   } finally {
     setOsmLoading(false);
     }
      },
  });
  };

  const handleOsmDownload = async () => {
    console.log('[OSM] handleOsmDownload called');
    console.log('[OSM] osmLoading:', osmLoading, 'osmPreprocessing:', osmPreprocessing);
  
    modal.confirm({
      title: t('osm.download.confirm') || 'Download OSM data file?',
      okText: t('yes') || 'Yes',
      cancelText: t('no') || 'No',
   onOk: async () => {
 console.log('[OSM] Download confirmed by user');
        try {
        setOsmLoading(true);
message.info((t('osm.download.data') || 'Downloading') + '...');
        console.log('[OSM] Calling osmPreprocessApi.download...');
   await osmPreprocessApi.download();
          console.log('[OSM] Download completed successfully');
      message.success(t('osm.download.success'));
  } catch (error: any) {
      console.error('[OSM] Error downloading OSM data:', error);
   message.error(error.message || t('osm.download.failed'));
    } finally {
       setOsmLoading(false);
   }
      },
    });
  };

  // Telegram Bot functions
  const fetchTelegramStatus = async () => {
    try {
      setTelegramLoading(true);
      const response = await telegramApi.getStatus();
      setTelegramStatus(response.data);
    } catch (error) {
      console.error('Error fetching Telegram status:', error);
    } finally {
      setTelegramLoading(false);
    }
  };

  const handleSetupWebhook = async () => {
    if (!webhookUrl.trim()) {
      message.error(t('telegram.webhook.url.required') || 'Webhook URL is required');
      return;
    }

    try {
      setTelegramLoading(true);
      const response = await telegramApi.setupWebhook(webhookUrl);
      if (response.data.success) {
        message.success(t('telegram.webhook.setup.success') || 'Webhook setup successfully');
        setIsWebhookModalVisible(false);
        setWebhookUrl('');
        await fetchTelegramStatus();
      } else {
        message.error(t('telegram.webhook.setup.failed') || 'Failed to setup webhook');
      }
    } catch (error: any) {
      console.error('Error setting up Telegram webhook:', error);
      message.error(error.response?.data?.error || t('telegram.webhook.setup.failed') || 'Failed to setup webhook');
    } finally {
      setTelegramLoading(false);
    }
  };

  const handleRemoveWebhook = async () => {
    try {
      setTelegramLoading(true);
      const response = await telegramApi.removeWebhook();
      if (response.data.success) {
        message.success(t('telegram.webhook.removed') || 'Webhook removed successfully');
        await fetchTelegramStatus();
      } else {
        message.error(t('telegram.webhook.remove.failed') || 'Failed to remove webhook');
      }
    } catch (error: any) {
      console.error('Error removing Telegram webhook:', error);
      message.error(error.response?.data?.error || t('telegram.webhook.remove.failed') || 'Failed to remove webhook');
    } finally {
      setTelegramLoading(false);
    }
  };

  const getTelegramModeTag = () => {
    if (!telegramStatus) return null;
    switch (telegramStatus.mode) {
      case 'webhook':
        return <Tag color="green">{t('telegram.mode.webhook') || 'Webhook'}</Tag>;
      case 'polling':
        return <Tag color="blue">{t('telegram.mode.polling') || 'Polling'}</Tag>;
      default:
        return <Tag color="default">{t('telegram.mode.disabled') || 'Disabled'}</Tag>;
    }
  };

  const aiModelColumns = [
    {
      title: t('model.name'),
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: AiModel) => (
        <Space>
          {text}
          {record.isSelected && <Tag color="green" icon={<CheckCircleOutlined />}>{t('selected.model')}</Tag>}
        </Space>
 ),
    },
    {
      title: t('model.description'),
      dataIndex: 'description',
    key: 'description',
    },
  {
      title: t('actions'),
   key: 'actions',
      render: (_: any, record: AiModel) => (
    <Space>
     {!record.isSelected && (
        <Button
              type="primary"
              size="small"
onClick={() => handleSelectAiModel(record.id)}
        disabled={aiModelLoading}
            >
         {t('select.model')}
            </Button>
      )}
 <Button
        type="default"
            size="small"
     icon={<EditOutlined />}
      onClick={() => handleEditAiModel(record)}
            disabled={aiModelLoading}
  />
    <Popconfirm
         title={t('confirm.delete.ai.model')}
            onConfirm={() => handleDeleteAiModel(record.id)}
            okText={t('yes')}
            cancelText={t('no')}
 disabled={record.isSelected}
          >
   <Button
   type="default"
    danger
         size="small"
  icon={<DeleteOutlined />}
     disabled={record.isSelected || aiModelLoading}
   />
   </Popconfirm>
        </Space>
      ),
    },
  ];

  if (loading) {
    return (
      <div className="container-fluid mt-4 d-flex justify-content-center">
        <Spin size="large" />
      </div>
    );
  }

  const groupedModels = groupModelsByPurpose();

  return (
 <div className="container-fluid mt-4">
      <Card>
        <Title level={2} style={{ marginBottom: 24 }}>
          <SettingOutlined style={{ marginRight: 8, color: '#1890ff' }} />
          {t('settings') || 'Settings'}
 </Title>
   <div className="row">
      <div className="col-md-6">
         <Form
            form={form}
  layout="vertical"
        onFinish={handleSave}
    initialValues={{
   language: language,
        theme: 'light',
       timezone: 'UTC',
       fuelStationLat: undefined,
       fuelStationLon: undefined
     }}
    >
    <Form.Item
      label={t('language') || 'Language'}
 name="language"
                rules={[{ required: true, message: t('please.select.language') || 'Please select a language' }]}
          >
   <Select size="large">
                  <Select.Option value="en">English</Select.Option>
       <Select.Option value="vi">Tiếng Việt</Select.Option>
     </Select>
        </Form.Item>
            <Form.Item
             label={t('theme') || 'Theme'}
         name="theme"
          >
    <Select size="large">
      <Select.Option value="light">{t('theme.light') || 'Light'}</Select.Option>
      <Select.Option value="dark">{t('theme.dark') || 'Dark'}</Select.Option>
       </Select>
           </Form.Item>
              <Form.Item
             label={t('timezone') || 'Timezone'}
           name="timezone"
              >
         <Select 
    size="large"
        showSearch
                  placeholder={t('select.timezone') || 'Select timezone'}
         >
 <Select.Option value="UTC">UTC</Select.Option>
       <Select.Option value="Asia/Ho_Chi_Minh">Asia/Ho Chi Minh</Select.Option>
                <Select.Option value="America/New_York">America/New York</Select.Option>
   <Select.Option value="Europe/London">Europe/London</Select.Option>
            <Select.Option value="Asia/Tokyo">Asia/Tokyo</Select.Option>
  </Select>
              </Form.Item>
              <Form.Item
                label={t('fuel.station.location') || 'Fuel Station Location'}
                tooltip={t('fuel.station.location.tooltip') || 'Used to detect fuel stops for fuel efficiency calculation. Get the coordinates from Google Maps by right-clicking the fuel station.'}
              >
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name="fuelStationLat"
                      noStyle
                      rules={[{ type: 'number', warningOnly: true }]}
                    >
                      <Input
                        size="large"
                        placeholder={t('fuel.station.lat') || 'Latitude (e.g. 10.266595)'}
                        type="number"
                        step="any"
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="fuelStationLon"
                      noStyle
                      rules={[{ type: 'number', warningOnly: true }]}
                    >
                      <Input
                        size="large"
                        placeholder={t('fuel.station.lon') || 'Longitude (e.g. 106.359337)'}
                        type="number"
                        step="any"
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Form.Item>
              <Form.Item>
       <Button
        type="primary"
     size="large"
       icon={<SaveOutlined />}
     htmlType="submit"
         loading={saving}
       >
       {t('save.settings') || 'Save Settings'}
       </Button>
              </Form.Item>
      </Form>
          </div>
 </div>
      </Card>

      {/* AI Model Configuration Section */}
      <Card 
        title={t('ai.model.configuration')}
        style={{ marginTop: 24 }}
     extra={
        <Button
    type="primary"
icon={<PlusOutlined />}
            onClick={handleAddAiModel}
       disabled={aiModelLoading}
   >
{t('add.ai.model')}
          </Button>
    }
      >
        <Spin spinning={aiModelLoading}>
          {Object.keys(groupedModels).length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
   <Typography.Text type="secondary">{t('no.ai.models.available')}</Typography.Text>
         </div>
          ) : (
            <Collapse defaultActiveKey={Object.keys(groupedModels)} style={{ marginBottom: 16 }}>
              {Object.entries(groupedModels).map(([purposeStr, models]) => {
          const purpose = parseInt(purposeStr) as ModelPurpose;
const selectedModel = models.find(m => m.isSelected);
         
    return (
      <Panel
   key={purpose}
   header={
                      <Space>
       <Tag color={getPurposeColor(purpose)}>{getPurposeLabel(purpose)}</Tag>
          {selectedModel && (
  <Tag color="green" icon={<CheckCircleOutlined />}>
{selectedModel.name}
       </Tag>
     )}
           <Typography.Text type="secondary">
        ({models.length} {models.length === 1 ? 'model' : 'models'})
        </Typography.Text>
   </Space>
        }
   >
   <Table
     dataSource={models}
            columns={aiModelColumns}
             rowKey="id"
       pagination={false}
     size="small"
        />
           </Panel>
      );
  })}
            </Collapse>
        )}
        </Spin>
      </Card>

   {/* AI Model Modal */}
      <Modal
   title={editingAiModel ? t('edit.ai.model') : t('add.ai.model')}
        open={isAiModelModalVisible}
     onOk={handleAiModelModalOk}
        onCancel={() => setIsAiModelModalVisible(false)}
        confirmLoading={aiModelLoading}
 width={600}
      >
  <Form form={aiModelForm} layout="vertical">
          <Form.Item
 label={t('model.purpose')}
            name="purpose"
       rules={[{ required: true, message: t('select.model.purpose') }]}
          >
            <Select placeholder={t('select.model.purpose')}>
<Select.Option value={ModelPurpose.DocumentTypeDetection}>
       <Tag color={getPurposeColor(ModelPurpose.DocumentTypeDetection)}>
 {getPurposeLabel(ModelPurpose.DocumentTypeDetection)}
             </Tag>
              </Select.Option>
       <Select.Option value={ModelPurpose.DocumentProcessing}>
         <Tag color={getPurposeColor(ModelPurpose.DocumentProcessing)}>
            {getPurposeLabel(ModelPurpose.DocumentProcessing)}
       </Tag>
      </Select.Option>
     <Select.Option value={ModelPurpose.DocumentProcessingPdf}>
   <Tag color={getPurposeColor(ModelPurpose.DocumentProcessingPdf)}>
       {getPurposeLabel(ModelPurpose.DocumentProcessingPdf)}
 </Tag>
  </Select.Option>
     <Select.Option value={ModelPurpose.DocumentProcessingImage}>
   <Tag color={getPurposeColor(ModelPurpose.DocumentProcessingImage)}>
      {getPurposeLabel(ModelPurpose.DocumentProcessingImage)}
        </Tag>
           </Select.Option>
              <Select.Option value={ModelPurpose.TelegramChat}>
                <Tag color={getPurposeColor(ModelPurpose.TelegramChat)}>
                  {getPurposeLabel(ModelPurpose.TelegramChat)}
                </Tag>
              </Select.Option>
   </Select>
          </Form.Item>
     <Form.Item
     label={t('model.name')}
            name="name"
          rules={[{ required: true, message: t('enter.model.name') }]}
          >
            <Input placeholder="qwen3-vl:8b or gemma-3-27b-it" />
     </Form.Item>
          <Form.Item
            label={t('model.description')}
            name="description"
          >
            <Input.TextArea rows={3} placeholder={t('model.description')} />
          </Form.Item>
        </Form>
</Modal>

      {/* Telegram Bot Configuration Section */}
      <Card 
        title={
          <Space>
            <ApiOutlined />
            {t('telegram.bot.configuration') || 'Telegram Bot Configuration'}
          </Space>
        }
        style={{ marginTop: 24 }}
      >
        <Spin spinning={telegramLoading}>
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Alert
                message={
                  <Space>
                    {t('telegram.bot.status') || 'Bot Status'}: 
                    {getTelegramModeTag()}
                    {telegramStatus?.enabled ? (
                      <Tag color="green">{t('enabled') || 'Enabled'}</Tag>
                    ) : (
                      <Tag color="red">{t('disabled') || 'Disabled'}</Tag>
                    )}
                  </Space>
                }
                description={
                  telegramStatus?.enabled ? (
                    <div>
                      <p style={{ marginBottom: 8 }}>
                        {t('telegram.bot.description') || 'The Telegram bot allows users to upload documents by sending images directly to the bot. Documents are processed using AI and saved as drafts.'}
                      </p>
                      <Typography.Text type="secondary">
                        {t('telegram.webhook.endpoint') || 'Webhook Endpoint'}: <code>{telegramStatus?.webhookEndpoint}</code>
                      </Typography.Text>
                    </div>
                  ) : (
                    <Typography.Text type="secondary">
                      {t('telegram.bot.disabled.message') || 'Configure the bot token in appsettings.json to enable the Telegram bot.'}
                    </Typography.Text>
                  )
                }
                type={telegramStatus?.enabled ? 'success' : 'info'}
                showIcon
              />
            </Col>

            <Col xs={24} md={12}>
              <Card size="small" title={t('telegram.chat.model') || 'Telegram Chat AI Model'}>
                {(() => {
                  const telegramModels = aiModels.filter(m => m.purpose === ModelPurpose.TelegramChat);
                  const selectedModel = telegramModels.find(m => m.isSelected);
                  return (
                    <Space direction="vertical" style={{ width: '100%' }}>
                      {selectedModel ? (
                        <Alert
                          message={
                            <Space>
                              <CheckCircleOutlined style={{ color: '#52c41a' }} />
                              <strong>{selectedModel.name}</strong>
                            </Space>
                          }
                          description={selectedModel.description || (t('telegram.chat.model.active') || 'This model handles Telegram bot chat responses with MCP tools.')}
                          type="success"
                        />
                      ) : (
                        <Alert
                          message={t('telegram.chat.model.not.configured') || 'No Telegram Chat model selected'}
                          description={t('telegram.chat.model.not.configured.desc') || 'Add a TelegramChat AI model to enable intelligent chat responses.'}
                          type="warning"
                          showIcon
                        />
                      )}
                      <Space wrap>
                        <Button
                          type={selectedModel ? 'default' : 'primary'}
                          icon={<PlusOutlined />}
                          size="small"
                          onClick={() => {
                            setEditingAiModel(null);
                            aiModelForm.resetFields();
                            aiModelForm.setFieldsValue({ purpose: ModelPurpose.TelegramChat });
                            setIsAiModelModalVisible(true);
                          }}
                        >
                          {t('add.ai.model')}
                        </Button>
                        {telegramModels.filter(m => !m.isSelected).map(model => (
                          <Button
                            key={model.id}
                            size="small"
                            onClick={() => handleSelectAiModel(model.id)}
                            disabled={aiModelLoading}
                          >
                            {t('select.model')}: {model.name}
                          </Button>
                        ))}
                      </Space>
                      {telegramModels.length > 0 && (
                        <Typography.Text type="secondary">
                          {telegramModels.length} {telegramModels.length === 1 ? 'model' : 'models'} {t('telegram.chat.model.available') || 'available'}
                        </Typography.Text>
                      )}
                    </Space>
                  );
                })()}
              </Card>
            </Col>

            {telegramStatus?.enabled && (
              <Col xs={24} md={12}>
                <Card size="small" title={t('telegram.webhook.configuration') || 'Webhook Configuration'}>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Typography.Paragraph type="secondary" style={{ marginBottom: 8 }}>
                      {t('telegram.webhook.instructions') || 'For production deployments, use webhook mode instead of polling for better performance and reliability.'}
                    </Typography.Paragraph>
                    
                    <Space wrap>
                      <Button
                        type="primary"
                        icon={<LinkOutlined />}
                        onClick={() => setIsWebhookModalVisible(true)}
                        disabled={telegramLoading}
                      >
                        {t('telegram.setup.webhook') || 'Setup Webhook'}
                      </Button>
                      
                      {telegramStatus?.mode === 'webhook' && (
                        <Popconfirm
                          title={t('telegram.remove.webhook.confirm') || 'Remove webhook and switch to polling mode?'}
                          onConfirm={handleRemoveWebhook}
                          okText={t('yes') || 'Yes'}
                          cancelText={t('no') || 'No'}
                        >
                          <Button
                            danger
                            icon={<DisconnectOutlined />}
                            disabled={telegramLoading}
                          >
                            {t('telegram.remove.webhook') || 'Remove Webhook'}
                          </Button>
                        </Popconfirm>
                      )}
                    </Space>
                  </Space>
                </Card>
              </Col>
            )}

            <Col span={24}>
              <Collapse ghost>
                <Panel key="usage" header={t('telegram.bot.usage') || 'How to Use'}>
                  <Typography.Paragraph>
                    <ol style={{ paddingLeft: 20 }}>
                      <li>{t('telegram.usage.step1') || 'Create a bot with @BotFather on Telegram'}</li>
                      <li>{t('telegram.usage.step2') || 'Copy the bot token to appsettings.json under Telegram:BotToken'}</li>
                      <li>{t('telegram.usage.step3') || 'Set Telegram:DefaultUserId to associate documents with a user'}</li>
                      <li>{t('telegram.usage.step4') || 'Restart the application'}</li>
                      <li>{t('telegram.usage.step5') || 'Send images of delivery notes or fuel receipts to your bot'}</li>
                    </ol>
                  </Typography.Paragraph>
                  <Alert
                    message={t('telegram.supported.documents') || 'Supported Documents'}
                    description={
                      <Space>
                        <Tag color="blue">{t('delivery.notes') || 'Delivery Notes'}</Tag>
                        <Tag color="green">{t('fuel.receipts') || 'Fuel Receipts'}</Tag>
                      </Space>
                    }
                    type="info"
                  />
                </Panel>
              </Collapse>
            </Col>
          </Row>
        </Spin>
      </Card>

      {/* System Export/Import Section */}
      <Card title={t('system.data.management')} style={{ marginTop: 24 }}>
        <Row gutter={16}>
     <Col span={12}>
  <Card>
        <div style={{ textAlign: 'center' }}>
      <h4>{t('export.all.data')}</h4>
   <p className="text-muted">{t('export.description')}</p>
      <Button
  type="primary"
    size="large"
         icon={<PlusOutlined />}
         onClick={handleExportData}
          loading={exportLoading}
           disabled={exportLoading}
 >
       {exportLoading ? t('exporting') : t('export.data')}
              </Button>
       </div>
            </Card>
  </Col>
    <Col span={12}>
      <Card>
           <div style={{ textAlign: 'center' }}>
   <h4>{t('import.data')}</h4>
    <p className="text-muted">{t('import.description')}</p>
  <input
    type="file"
        ref={fileInputRef}
      accept=".json"
     style={{ display: 'none' }}
        onChange={handleFileSelect}
   />
<Button
  type="primary"
         size="large"
             icon={<PlusOutlined />}
      onClick={() => fileInputRef.current?.click()}
  loading={importLoading}
      disabled={importLoading}
     >
      {importLoading ? t('importing') : t('select.import.file')}
  </Button>
    </div>
     </Card>
        </Col>
  </Row>
    {systemInfo && (
        <div style={{ marginTop: 16 }}>
            <h5>{t('system.information')}</h5>
   <Row gutter={16}>
              <Col span={8}>
 <Statistic title={t('total.shipping.records')} value={systemInfo.databaseInfo.totalShippingRecords} />
   </Col>
              <Col span={8}>
  <Statistic title={t('total.drivers')} value={systemInfo.databaseInfo.totalDrivers} />
  </Col>
      <Col span={8}>
        <Statistic title={t('total.vehicles')} value={systemInfo.databaseInfo.totalVehicles} />
  </Col>
    </Row>
   </div>
        )}
      </Card>

      {/* OSM Data Preprocessing Section */}
      <Card 
        title={
          <Space>
            <DatabaseOutlined />
     {t('osm.data.management')}
      </Space>
        }
        style={{ marginTop: 24 }}
      >
        <Spin spinning={osmLoading || osmPreprocessing}>
          {osmStatus?.hasData ? (
      <Alert
       message={t('osm.has.data')}
              description={
       <div>
    <p>{t('osm.benefits.title')}:</p>
<ul>
  <li>{t('osm.benefits.memory')}</li>
        <li>{t('osm.benefits.startup')}</li>
         <li>{t('osm.benefits.performance')}</li>
      </ul>
         </div>
              }
  type="success"
  showIcon
              style={{ marginBottom: 16 }}
            />
        ) : (
            <Alert
message={t('osm.no.data')}
       description="Preprocessing OSM data will improve routing performance and reduce memory usage."
   type="info"
          showIcon
    style={{ marginBottom: 16 }}
            />
          )}

          {osmStatus?.hasData && (
      <div style={{ marginBottom: 16 }}>
  <Title level={5}>{t('osm.statistics')}</Title>
     <Row gutter={16}>
 <Col span={6}>
   <Statistic 
     title={t('osm.nodes')} 
        value={osmStatus.statistics.nodes} 
    formatter={(value) => value.toLocaleString()}
        />
              </Col>
  <Col span={6}>
<Statistic 
        title={t('osm.road.segments')} 
      value={osmStatus.statistics.roadSegments}
      formatter={(value) => value.toLocaleString()}
        />
        </Col>
           <Col span={6}>
       <Statistic 
  title={t('osm.address.points')} 
           value={osmStatus.statistics.addressPoints}
  formatter={(value) => value.toLocaleString()}
      />
     </Col>
            <Col span={6}>
          <Statistic 
   title={t('osm.boundaries')} 
                  value={osmStatus.statistics.administrativeBoundaries}
          formatter={(value) => value.toLocaleString()}
      />
   </Col>
  </Row>

          <div style={{ marginTop: 16 }}>
                <Title level={5}>{t('osm.estimated.db.size')}</Title>
    <Progress 
         percent={100}
     format={() => {
         const totalSizeMB = 
               (osmStatus.estimatedDatabaseSizeMB?.nodes || 0) +
             (osmStatus.estimatedDatabaseSizeMB?.segments || 0) +
     (osmStatus.estimatedDatabaseSizeMB?.addresses || 0) +
   (osmStatus.estimatedDatabaseSizeMB?.boundaries || 0);
          return `${totalSizeMB.toFixed(2)} MB`;
       }}
             strokeColor="#52c41a"
      />
              </div>
          </div>
    )}

       <Space size="middle" wrap>
            <Button
          type="primary"
          icon={<PlayCircleOutlined />}
    onClick={handleOsmPreprocess}
         loading={osmPreprocessing}
disabled={osmLoading || osmPreprocessing}
  >
         {t('osm.start.preprocessing')}
   </Button>
            
     {osmStatus?.hasData && (
       <Popconfirm
     title={t('osm.clear.confirm')}
       onConfirm={handleOsmClear}
       okText={t('yes')}
           cancelText={t('no')}
                okType="danger"
              >
   <Button
  danger
      icon={<ClearOutlined />}
      disabled={osmLoading || osmPreprocessing}
        >
                  {t('osm.clear.data')}
    </Button>
     </Popconfirm>
    )}
    
            <Button
     icon={<CloudDownloadOutlined />}
              onClick={handleOsmDownload}
         disabled={osmLoading || osmPreprocessing}
        >
    {t('osm.download.data')}
         </Button>
          </Space>

          {osmPreprocessing && (
 <Alert
   message={t('osm.preprocessing.in.progress')}
 type="warning"
            showIcon
           style={{ marginTop: 16 }}
            />
   )}
        </Spin>
  </Card>

      {/* Webhook Setup Modal */}
      <Modal
        title={t('telegram.setup.webhook') || 'Setup Telegram Webhook'}
        open={isWebhookModalVisible}
        onOk={handleSetupWebhook}
        onCancel={() => {
          setIsWebhookModalVisible(false);
          setWebhookUrl('');
        }}
        confirmLoading={telegramLoading}
        okText={t('setup') || 'Setup'}
        cancelText={t('cancel') || 'Cancel'}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Alert
            message={t('telegram.webhook.info') || 'Webhook URL'}
            description={t('telegram.webhook.info.description') || 'Enter the full HTTPS URL where Telegram will send updates. This must be a publicly accessible URL with a valid SSL certificate.'}
            type="info"
            showIcon
          />
          <Form layout="vertical">
            <Form.Item
              label={t('telegram.webhook.url') || 'Webhook URL'}
              required
              help={t('telegram.webhook.url.example') || 'Example: https://your-domain.com/api/telegram/webhook'}
            >
              <Input
                placeholder="https://your-domain.com/api/telegram/webhook"
                value={webhookUrl}
                onChange={(e) => setWebhookUrl(e.target.value)}
                prefix={<LinkOutlined />}
              />
            </Form.Item>
          </Form>
        </Space>
      </Modal>
    </div>
  );
};

export default SettingsPage;